import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../../../lib/utils"
import {
  type IconSemanticColor,
  iconSemanticVariants,
  normalizeCssVar,
  resolveIconColorVarStyle,
} from "./icon-color"
import { getIconComponent, type IconName } from "./icon-registry"

/** `block` avoids inline-SVG baseline gap next to text inside flex buttons */
const iconSizeVariants = cva("block shrink-0", {
  variants: {
    size: {
      xs: "size-3",
      sm: "size-3.5",
      md: "size-4",
      lg: "size-5",
      xl: "size-6",
    },
  },
  defaultVariants: {
    size: "md",
  },
})

export type IconProps = Omit<React.SVGProps<SVGSVGElement>, "color" | "name"> &
  VariantProps<typeof iconSizeVariants> & {
    /** Registry key — see `ICON_NAMES` or Storybook **Design System / Icon** */
    name: IconName
    /**
     * Semantic text color → Tailwind `text-*` tied to design tokens.
     * Ignored when `colorVar` is set.
     */
    color?: IconSemanticColor
    /**
     * Design-system **CSS custom property** for icon `color` (`--foreground`, `chart-1`, … — with or without `--` prefix).
     * Overrides `color`. Opaque by default; use `colorVarOpacity` &lt; 1 for {@link colorToken} translucency.
     */
    colorVar?: string
    /**
     * 0–1, default **1**. Only applies with `colorVar`. At 1, uses `var(--your-var)`; below 1, uses `colorToken(var, opacity)`.
     */
    colorVarOpacity?: number
  }

export type { IconName, IconSemanticColor }
export { ICON_NAMES, iconRegistry, getIconComponent } from "./icon-registry"
export {
  ICON_SEMANTIC_COLOR_OPTIONS,
  iconSemanticColorClasses,
  normalizeCssVar,
  resolveIconColorVarStyle,
} from "./icon-color"

const Icon = React.forwardRef<SVGSVGElement, IconProps>(function Icon(
  {
    name,
    size,
    color = "default",
    colorVar,
    colorVarOpacity = 1,
    className,
    style,
    ...props
  },
  ref,
) {
  const Lucide = getIconComponent(name)
  const fromColorVar = colorVar != null && colorVar.length > 0
  const colorVarStyle = fromColorVar ? resolveIconColorVarStyle(colorVar, colorVarOpacity) : undefined

  if (!Lucide) return null

  return (
    <Lucide
      ref={ref}
      data-slot="icon"
      data-icon={name}
      {...(fromColorVar ? { "data-icon-color-var": normalizeCssVar(colorVar) } : {})}
      aria-hidden
      focusable={false}
      className={cn(
        iconSizeVariants({ size }),
        !fromColorVar && iconSemanticVariants({ color }),
        className,
      )}
      style={{ ...colorVarStyle, ...style }}
      {...props}
    />
  )
})

export { Icon, iconSizeVariants, iconSemanticVariants }
